import { useScrollReveal } from "@/hooks/useScrollReveal";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageTitle from "@/components/base/PageTitle";
import { Link } from "react-router-dom";

const privacyHighlights = [
  {
    icon: "ri-shield-user-line",
    title: "WHAT WE COLLECT",
    desc: "We only collect information you voluntarily provide through our contact and consultation forms: name, email address, phone number, and your message or consultation preferences.",
  },
  {
    icon: "ri-shield-check-line",
    title: "HOW WE USE IT",
    desc: "Your information is used solely to respond to inquiries, schedule consultations, communicate about diamond sourcing requests, and deliver related customer service.",
  },
  {
    icon: "ri-delete-bin-line",
    title: "RETENTION PERIOD",
    desc: "We retain form submissions for up to 90 days to fulfill your request and provide follow-up support, after which they are permanently deleted from our systems.",
  },
  {
    icon: "ri-hand-coin-line",
    title: "NO DATA SALES",
    desc: "We do not sell, trade, rent, or otherwise disclose your personal information to third parties for marketing or commercial purposes.",
  },
  {
    icon: "ri-eye-off-line",
    title: "NO TRACKING COOKIES",
    desc: "We do not use cookies, analytics tools, tracking pixels, or any behavioral tracking technologies on this website.",
  },
  {
    icon: "ri-bank-card-line",
    title: "NO ONLINE PAYMENTS",
    desc: "This website does not process online payments. All transactions are handled through direct invoicing and payment arrangements outside of this website.",
  },
];

export default function PrivacyPolicy() {
  const headerRef = useScrollReveal<HTMLDivElement>();
  const highlightsRef = useScrollReveal<HTMLDivElement>();
  const fullPolicyRef = useScrollReveal<HTMLDivElement>();
  const ctaRef = useScrollReveal<HTMLDivElement>();
  const summaryRef = useScrollReveal<HTMLDivElement>();

  return (
    <div className="min-h-screen bg-zayan-bg flex flex-col">
      <PageTitle title="Privacy Policy - Zayan Atelier" />
      <Navbar />

      <section className="pt-[130px] md:pt-[170px] pb-16 px-6 lg:px-16">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16 reveal" ref={headerRef}>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-zayan-gold text-xs tracking-[0.2em] font-sans">PRIVACY POLICY</span>
                <div className="h-px w-12 bg-zayan-gold" />
              </div>
              <h2 className="font-serif text-4xl lg:text-6xl text-zayan-gold leading-tight mb-6">
                Privacy Policy
              </h2>
              <div className="h-px w-16 bg-zayan-gold mb-8" />
              <p className="text-zayan-textMuted text-sm leading-relaxed max-w-md">
                At Zayan Atelier, your privacy matters. This policy explains what information we collect, how we use it, and how we protect your personal data.
              </p>
            </div>
            <div className="relative overflow-hidden rounded-lg border border-zayan-border h-64 lg:h-auto">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20abstract%20geometric%20pattern%20with%20subtle%20gold%20and%20black%20tones%2C%20dark%20background%2C%20luxury%20jewelry%20aesthetic%2C%20soft%20gradients%2C%20modern%20and%20refined%2C%20high%20contrast%20with%20muted%20light%20accents%2C%20abstract%20artistic%20composition%2C%20no%20text%20no%20watermark&width=700&height=450&seq=privacy-hero-1&orientation=landscape"
                alt="Privacy abstract"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-zayan-bg/50 to-transparent" />
            </div>
          </div>

          {/* Summary of Key Points */}
          <div className="mb-16 reveal" ref={summaryRef}>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-zayan-gold text-xs tracking-[0.2em] font-sans">AT A GLANCE</span>
              <div className="h-px w-12 bg-zayan-gold" />
            </div>
            <h3 className="font-serif text-2xl lg:text-3xl text-zayan-gold mb-8">Summary of Key Points</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
              <div>
                <p className="text-zayan-gold text-sm tracking-[0.1em] font-sans mb-1">WHAT INFORMATION DO WE COLLECT?</p>
                <p className="text-zayan-textMuted text-sm leading-relaxed">Only what you voluntarily provide: your name, email, phone number, and any messages or consultation preferences submitted through our forms.</p>
              </div>
              <div>
                <p className="text-zayan-gold text-sm tracking-[0.1em] font-sans mb-1">DO WE PROCESS SENSITIVE INFORMATION?</p>
                <p className="text-zayan-textMuted text-sm leading-relaxed">No. We do not collect payment card information, government IDs, biometric data, or any other sensitive personal information.</p>
              </div>
              <div>
                <p className="text-zayan-gold text-sm tracking-[0.1em] font-sans mb-1">DO WE COLLECT PAYMENT INFORMATION?</p>
                <p className="text-zayan-textMuted text-sm leading-relaxed">No. This website does not process online payments. All purchases and payment arrangements are handled privately outside of the website.</p>
              </div>
              <div>
                <p className="text-zayan-gold text-sm tracking-[0.1em] font-sans mb-1">DO WE SELL YOUR PERSONAL INFORMATION?</p>
                <p className="text-zayan-textMuted text-sm leading-relaxed">No. We do not sell, rent, trade, or share your personal information for advertising, marketing, or commercial purposes.</p>
              </div>
              <div>
                <p className="text-zayan-gold text-sm tracking-[0.1em] font-sans mb-1">DO WE USE COOKIES OR TRACKING TECHNOLOGIES?</p>
                <p className="text-zayan-textMuted text-sm leading-relaxed">We do not use cookies, analytics tools, tracking pixels, or behavioral tracking technologies for marketing or advertising purposes.</p>
              </div>
              <div>
                <p className="text-zayan-gold text-sm tracking-[0.1em] font-sans mb-1">HOW LONG DO WE KEEP YOUR INFORMATION?</p>
                <p className="text-zayan-textMuted text-sm leading-relaxed">We retain submitted information for up to 90 days, after which it is permanently deleted from our systems.</p>
              </div>
              <div>
                <p className="text-zayan-gold text-sm tracking-[0.1em] font-sans mb-1">WHAT RIGHTS DO YOU HAVE?</p>
                <p className="text-zayan-textMuted text-sm leading-relaxed">You may request access to, correction of, or deletion of your personal information at any time by contacting us.</p>
              </div>
              <div>
                <p className="text-zayan-gold text-sm tracking-[0.1em] font-sans mb-1">QUESTIONS OR CONCERNS?</p>
                <p className="text-zayan-textMuted text-sm leading-relaxed">Contact us anytime at <a href="mailto:hello@zayan-atelier.com" className="text-zayan-gold hover:underline">hello@zayan-atelier.com</a>.</p>
              </div>
            </div>
          </div>

          {/* Quick Highlights Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0 border-t border-zayan-border reveal" ref={highlightsRef}>
            {privacyHighlights.map((h, i) => (
              <div key={i} className="flex items-start gap-5 py-8 border-b border-zayan-border hover:bg-zayan-gold/5 transition-colors duration-500">
                <div className="w-14 h-14 flex items-center justify-center rounded-full border border-zayan-gold/40 shrink-0">
                  <i className={`${h.icon} text-zayan-gold text-xl`} />
                </div>
                <div>
                  <h3 className="text-zayan-gold text-sm tracking-[0.15em] font-sans mb-2">{h.title}</h3>
                  <p className="text-zayan-textMuted text-sm leading-relaxed">{h.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Full Privacy Policy */}
          <div className="mt-16 border border-zayan-border bg-zayan-bgCard p-8 lg:p-12 reveal" ref={fullPolicyRef}>
            <h3 className="font-serif text-3xl text-zayan-gold mb-2">Full Privacy Policy</h3>
            <p className="text-zayan-textMuted text-xs mb-10">Effective Date: May 2026</p>

            <div className="space-y-10">
              <div>
                <h4 className="text-zayan-gold text-sm tracking-[0.15em] font-sans mb-3">1. INTRODUCTION</h4>
                <p className="text-zayan-textMuted text-sm leading-relaxed">
                  This Privacy Policy describes how Zayan Atelier collects, uses, stores, and protects your personal information when you visit our website or submit inquiries through our contact and consultation forms. By using this website or submitting your information, you consent to the practices described in this policy.
                </p>
              </div>

              <div>
                <h4 className="text-zayan-gold text-sm tracking-[0.15em] font-sans mb-3">2. INFORMATION WE COLLECT</h4>
                <p className="text-zayan-textMuted text-sm leading-relaxed mb-3">
                  We collect only the information you voluntarily provide to us. This includes:
                </p>
                <ul className="text-zayan-textMuted text-sm leading-relaxed list-disc list-inside space-y-1">
                  <li><strong className="text-zayan-gold">Contact Information:</strong> Your full name, email address, and phone number.</li>
                  <li><strong className="text-zayan-gold">Messages &amp; Preferences:</strong> Any details you include in your message, consultation requests, or diamond sourcing specifications.</li>
                  <li><strong className="text-zayan-gold">Technical Information:</strong> Basic server log data processed by our website security and content delivery infrastructure, including IP address, browser type, and pages visited. This is used for website security and performance and is not used to identify individual users.</li>
                </ul>
              </div>

              <div>
                <h4 className="text-zayan-gold text-sm tracking-[0.15em] font-sans mb-3">3. HOW WE USE YOUR INFORMATION</h4>
                <p className="text-zayan-textMuted text-sm leading-relaxed mb-3">
                  We use the information we collect for the following purposes:
                </p>
                <ul className="text-zayan-textMuted text-sm leading-relaxed list-disc list-inside space-y-1">
                  <li>To respond to your inquiries and provide customer support.</li>
                  <li>To schedule and confirm consultations.</li>
                  <li>To communicate about diamond sourcing requests, quotations, and order updates.</li>
                  <li>To improve our services and website functionality.</li>
                  <li>To comply with legal obligations.</li>
                </ul>
              </div>

              <div>
                <h4 className="text-zayan-gold text-sm tracking-[0.15em] font-sans mb-3">4. THIRD-PARTY SERVICE PROVIDERS</h4>
                <p className="text-zayan-textMuted text-sm leading-relaxed mb-3">
                  We use trusted third-party service providers to operate our website and communicate with you. These providers fall into the following categories:
                </p>
                <ul className="text-zayan-textMuted text-sm leading-relaxed list-disc list-inside space-y-1">
                  <li><strong className="text-zayan-gold">Form Processing Services:</strong> We use a secure form processing service to receive and deliver your form submissions (including your name, email, phone, and message) to our business email. This provider acts solely as a data processor on our behalf and does not use your data for its own purposes.</li>
                  <li><strong className="text-zayan-gold">Website Security &amp; Performance Services:</strong> We use a content delivery and website security service for performance optimization, protection against malicious traffic, and secure content delivery. This provider may process basic technical data such as IP addresses for security screening and routing purposes only.</li>
                </ul>
                <p className="text-zayan-textMuted text-sm leading-relaxed mt-3">
                  We do not sell, rent, or disclose your personal information to any other third parties for marketing, advertising, or commercial purposes.
                </p>
              </div>

              <div>
                <h4 className="text-zayan-gold text-sm tracking-[0.15em] font-sans mb-3">5. DATA RETENTION</h4>
                <p className="text-zayan-textMuted text-sm leading-relaxed">
                  We retain your form submissions and associated communications for up to 90 days from the date of submission. This period allows us to fulfill your request, provide follow-up support, and address any related inquiries. After 90 days, your information is permanently deleted from our systems. We do not maintain long-term databases of customer inquiries.
                </p>
              </div>

              <div>
                <h4 className="text-zayan-gold text-sm tracking-[0.15em] font-sans mb-3">6. COOKIES AND TRACKING TECHNOLOGIES</h4>
                <p className="text-zayan-textMuted text-sm leading-relaxed">
                  We do not use cookies, web beacons, analytics scripts, tracking pixels, or any other behavioral tracking technologies on this website. We do not monitor your browsing behavior across other websites or platforms.
                </p>
              </div>

              <div>
                <h4 className="text-zayan-gold text-sm tracking-[0.15em] font-sans mb-3">7. DATA SECURITY</h4>
                <p className="text-zayan-textMuted text-sm leading-relaxed">
                  We take reasonable measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. Form submissions are transmitted securely via HTTPS and processed through trusted third-party services that maintain industry-standard security practices. However, no method of transmission over the internet is completely secure, and we cannot guarantee absolute security.
                </p>
              </div>

              <div>
                <h4 className="text-zayan-gold text-sm tracking-[0.15em] font-sans mb-3">8. YOUR RIGHTS AND CHOICES</h4>
                <p className="text-zayan-textMuted text-sm leading-relaxed mb-3">
                  You have the right to:
                </p>
                <ul className="text-zayan-textMuted text-sm leading-relaxed list-disc list-inside space-y-1">
                  <li>Request access to the personal information we hold about you.</li>
                  <li>Request correction or updates to inaccurate information.</li>
                  <li>Request deletion of your personal information before the standard 90-day retention period.</li>
                  <li>Opt out of future communications at any time by contacting us.</li>
                </ul>
                <p className="text-zayan-textMuted text-sm leading-relaxed mt-3">
                  To exercise any of these rights, please contact us at <a href="mailto:hello@zayan-atelier.com" className="text-zayan-gold hover:underline">hello@zayan-atelier.com</a>.
                </p>
              </div>

              <div>
                <h4 className="text-zayan-gold text-sm tracking-[0.15em] font-sans mb-3">9. CHILDREN'S PRIVACY</h4>
                <p className="text-zayan-textMuted text-sm leading-relaxed">
                  This website is not intended for children under the age of 13, and we do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected information from a child under 13, please contact us immediately at <a href="mailto:hello@zayan-atelier.com" className="text-zayan-gold hover:underline">hello@zayan-atelier.com</a> and we will promptly delete the information.
                </p>
              </div>

              <div>
                <h4 className="text-zayan-gold text-sm tracking-[0.15em] font-sans mb-3">10. ONLINE PAYMENTS</h4>
                <p className="text-zayan-textMuted text-sm leading-relaxed">
                  This website does not accept, process, or store any online payments. All financial transactions are conducted through direct invoicing and external payment arrangements communicated to you separately. No payment card information is collected through this website.
                </p>
              </div>

              <div>
                <h4 className="text-zayan-gold text-sm tracking-[0.15em] font-sans mb-3">11. AI-BASED SERVICES</h4>
                <p className="text-zayan-textMuted text-sm leading-relaxed">
                  Zayan Atelier does not offer any AI-based services through this website. All consultations, recommendations, and communications are provided directly by our team. No automated decision-making, profiling, or AI-generated recommendations are used in our client interactions.
                </p>
              </div>

              <div>
                <h4 className="text-zayan-gold text-sm tracking-[0.15em] font-sans mb-3">12. POLICY UPDATES</h4>
                <p className="text-zayan-textMuted text-sm leading-relaxed">
                  We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or website functionality. Any changes will be posted on this page with an updated effective date. We encourage you to review this policy periodically.
                </p>
              </div>

              <div>
                <h4 className="text-zayan-gold text-sm tracking-[0.15em] font-sans mb-3">13. CONTACT US</h4>
                <p className="text-zayan-textMuted text-sm leading-relaxed">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="mt-3 text-zayan-textMuted text-sm leading-relaxed">
                  <p><strong className="text-zayan-gold">Email:</strong> <a href="mailto:hello@zayan-atelier.com" className="text-zayan-gold hover:underline">hello@zayan-atelier.com</a></p>
                  <p><strong className="text-zayan-gold">Location:</strong> El Paso, TX</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Banner */}
          <div className="mt-12 border border-zayan-border bg-zayan-bgCard px-6 lg:px-12 py-10 flex flex-col md:flex-row items-center justify-between gap-6 reveal" ref={ctaRef}>
            <div className="flex items-center gap-6">
              <img
                src="https://static.readdy.ai/image/f050cadc4e04390e0c8f3be3f5e83a4a/754daf6fa5a838036e698ab0e430b19f.png"
                alt="Zayan Atelier"
                className="h-16 w-auto opacity-70"
              />
              <div className="h-12 w-px bg-zayan-border hidden md:block" />
              <div>
                <h3 className="font-serif text-2xl text-zayan-gold mb-1">Still Have Questions?</h3>
                <p className="text-zayan-textMuted text-sm">If you have any concerns about your privacy or data, please reach out. We're happy to clarify anything.</p>
              </div>
            </div>
            <Link
              to="/contact"
              className="shrink-0 border border-zayan-gold text-zayan-gold px-8 py-3 text-xs tracking-[0.15em] hover:bg-zayan-gold hover:text-zayan-bg transition-colors whitespace-nowrap"
            >
              CONTACT US
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}